import { useEffect, useMemo, useState } from 'react'
import { getMembershipConfirmation, isCheckoutSessionId } from '../utils/practitionerApi'
import type { MembershipPlanId } from '../types/practitionerSignup'

export type ConfirmationStatus =
  | 'verifying'
  | 'confirmed'
  | 'processing'
  | 'failed'
  | 'missing'
  | 'unavailable'

// The Stripe webhook can land a few seconds after the redirect — poll briefly.
const MAX_ATTEMPTS = 8
const POLL_INTERVAL_MS = 2500

/**
 * Confirms the practitioner's membership with the backend after Stripe
 * Checkout. Only returns 'confirmed' when the backend (webhook-written
 * practitioner_subscriptions) says so.
 *
 * `preview` is used solely by the design canvas to render each state; it is
 * ignored in normal page loads (see PractitionerWelcomePage).
 */
export function useMembershipConfirmation(preview?: ConfirmationStatus) {
  const sessionId = useMemo(() => new URLSearchParams(window.location.search).get('session_id'), [])
  const hasSession = isCheckoutSessionId(sessionId)

  const [status, setStatus] = useState<ConfirmationStatus>(
    preview ?? (hasSession ? 'verifying' : 'missing'),
  )
  const [planId, setPlanId] = useState<MembershipPlanId | null>(null)
  const [trialEndDate, setTrialEndDate] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (preview || !isCheckoutSessionId(sessionId)) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let tries = 0
    setStatus('verifying')

    const poll = async () => {
      tries += 1
      try {
        const result = await getMembershipConfirmation(sessionId)
        if (cancelled) return
        if (result.status === 'confirmed') {
          setPlanId(result.tierName)
          setTrialEndDate(result.trialEndDate)
          setStatus('confirmed')
          return
        }
        if (result.status === 'failed') {
          setStatus('failed')
          return
        }
        if (tries >= MAX_ATTEMPTS) {
          setStatus('processing')
          return
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS)
      } catch {
        if (!cancelled) setStatus('unavailable')
      }
    }

    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [preview, sessionId, attempt])

  return {
    status,
    planId,
    trialEndDate,
    retry: () => setAttempt((n) => n + 1),
  }
}
